/**
 * Unified Demand Forecast Service
 *
 * Combines recurring orders + AI predictions into a single calibrated forecast.
 * This is the single source of truth for demand forecasting in the Sales Timeline.
 *
 * Pipeline:
 * 1. Collect demand sources (recurring orders + AI predictions)
 * 2. Aggregate by week for calibration
 * 3. Apply statistical calibration (Bayesian bounds)
 * 4. Distribute back to daily forecasts
 * 5. Upsert to demand_forecasts table
 */

import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'
import {
  calculateBayesianBounds,
  getSeasonalFactor
} from './forecastStatistics'

// =============================================================================
// CONFIGURATION
// =============================================================================

const CONFIG = {
  // Forecast horizon (days ahead to generate)
  HORIZON_DAYS: 180,

  // Calibration settings
  CALIBRATION: {
    ENABLED: true,
    ROLLING_WEEKS: 26,    // 6-month rolling window for statistics
    SIGMA_BOUNDS: 2,      // Number of standard deviations for bounds
    MIN_WEEKS_FOR_STATS: 4 // Minimum weeks of data before calibration kicks in
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get Monday of the week containing the given date
 * @param {Date|string} date - Date to get week start for
 * @returns {string} Monday date in YYYY-MM-DD format
 */
function getWeekStart(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return toLocalDateString(d)
}

/**
 * Format date as YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} Formatted date
 */
function formatDate(date) {
  return toLocalDateString(date)
}

/**
 * Generate array of dates from start to end (inclusive)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {string[]} Array of date strings
 */
function generateDateRange(startDate, endDate) {
  const dates = []
  const current = new Date(startDate)
  const end = new Date(endDate)

  while (current <= end) {
    dates.push(formatDate(current))
    current.setDate(current.getDate() + 1)
  }

  return dates
}

// =============================================================================
// DATA FETCHING
// =============================================================================

/**
 * Fetch recurring order demand per day per strain
 * @param {string} startDate - Start of forecast period
 * @param {string} endDate - End of forecast period
 * @returns {Promise<Map>} Map of date|strainId -> quantity
 */
async function fetchRecurringDemand(startDate, endDate) {
  const { data: instances, error } = await supabase
    .from('recurring_order_instances')
    .select(`
      scheduled_date,
      status,
      recurring_order:recurring_orders (
        recurring_order_items (
          quantity,
          products:product_id (
            strain
          )
        )
      )
    `)
    .eq('status', 'pending')
    .gte('scheduled_date', startDate)
    .lte('scheduled_date', endDate)

  if (error) {
    console.error('[demandForecastService] Error fetching recurring orders:', error)
    throw error
  }

  // Aggregate by date and strain
  const demandMap = new Map()

  for (const instance of instances || []) {
    const order = instance.recurring_order
    if (!order) continue

    for (const item of order.recurring_order_items || []) {
      const strainName = item.products?.strain
      if (!strainName) continue

      const key = `${instance.scheduled_date}|${strainName}`
      const existing = demandMap.get(key) || 0
      demandMap.set(key, existing + (parseFloat(item.quantity) || 0))
    }
  }

  return demandMap
}

/**
 * Fetch AI prediction demand per day per strain
 * @param {string} startDate - Start of forecast period
 * @param {string} endDate - End of forecast period
 * @returns {Promise<Map>} Map of date|strainId -> quantity
 */
async function fetchPredictionDemand(startDate, endDate) {
  const { data: suggestions, error } = await supabase
    .from('sale_suggestions')
    .select(`
      suggested_date,
      suggested_quantity,
      product:products (
        strain
      )
    `)
    .eq('status', 'pending')
    .gte('suggested_date', startDate)
    .lte('suggested_date', endDate)

  if (error) {
    console.error('[demandForecastService] Error fetching predictions:', error)
    throw error
  }

  // Aggregate by date and strain
  const demandMap = new Map()

  for (const suggestion of suggestions || []) {
    const strainName = suggestion.product?.strain
    if (!strainName) continue

    const key = `${suggestion.suggested_date}|${strainName}`
    const existing = demandMap.get(key) || 0
    demandMap.set(key, existing + (parseFloat(suggestion.suggested_quantity) || 0))
  }

  return demandMap
}

/**
 * Fetch strain ID lookup (name -> id)
 * @returns {Promise<Map>} Map of strain name -> strain id
 */
async function fetchStrainLookup() {
  const { data: strains, error } = await supabase
    .from('strains')
    .select('id, name')
    .eq('active', true)

  if (error) {
    console.error('[demandForecastService] Error fetching strains:', error)
    throw error
  }

  const lookup = new Map()
  for (const strain of strains || []) {
    lookup.set(strain.name, strain.id)
  }

  return lookup
}

/**
 * Fetch rolling statistics per strain from database
 * @param {number} weeks - Rolling window in weeks
 * @returns {Promise<Map>} Map of strainId -> { avg_weekly_kg, stddev_weekly_kg, week_count }
 */
async function fetchRollingStatistics(weeks = 26) {
  const { data, error } = await supabase
    .rpc('get_strain_rolling_statistics', {
      p_strain_id: null,
      p_weeks: weeks
    })

  if (error) {
    // If function doesn't exist, return empty map (calibration will be skipped)
    if (error.code === 'PGRST202') {
      console.warn('[demandForecastService] Rolling statistics function not found, skipping calibration')
      return new Map()
    }
    console.error('[demandForecastService] Error fetching rolling stats:', error)
    throw error
  }

  const statsMap = new Map()
  for (const row of data || []) {
    statsMap.set(row.strain_id, {
      avg_weekly_kg: parseFloat(row.avg_weekly_kg) || 0,
      stddev_weekly_kg: parseFloat(row.stddev_weekly_kg) || 0,
      week_count: row.week_count || 0,
      strain_name: row.strain_name
    })
  }

  return statsMap
}

// =============================================================================
// CALIBRATION
// =============================================================================

/**
 * Apply calibration to weekly totals using Bayesian bounds
 * @param {Map} weeklyTotals - Map of weekStart|strainId -> { recurring, predicted, total }
 * @param {Map} rollingStats - Map of strainId -> stats
 * @returns {Map} Map of weekStart|strainId -> { calibrated, factor }
 */
function calibrateWeeklyTotals(weeklyTotals, rollingStats) {
  const calibrated = new Map()

  for (const [key, totals] of weeklyTotals) {
    const [weekStart, strainId] = key.split('|')
    const stats = rollingStats.get(strainId)

    // Default: no calibration
    let calibratedKg = totals.total
    let factor = 1.0

    // Apply calibration if we have sufficient statistics
    if (stats && stats.week_count >= CONFIG.CALIBRATION.MIN_WEEKS_FOR_STATS) {
      // Get month for seasonal adjustment
      const weekDate = new Date(weekStart)
      const month = weekDate.getMonth() + 1
      const seasonalFactor = getSeasonalFactor(month)

      // Apply seasonal adjustment to mean and stddev
      const seasonalMean = stats.avg_weekly_kg * seasonalFactor
      const seasonalStddev = stats.stddev_weekly_kg * seasonalFactor

      // Calculate Bayesian bounds
      const bounds = calculateBayesianBounds(
        totals.total,
        seasonalMean,
        seasonalStddev,
        CONFIG.CALIBRATION.SIGMA_BOUNDS
      )

      calibratedKg = bounds.bounded
      factor = totals.total > 0 ? calibratedKg / totals.total : 1.0

      if (bounds.wasConstrained) {
        console.log(
          `[demandForecastService] Calibrated ${stats.strain_name} week ${weekStart}: ` +
          `${totals.total.toFixed(1)}kg → ${calibratedKg.toFixed(1)}kg (factor: ${factor.toFixed(2)})`
        )
      }
    }

    calibrated.set(key, {
      calibrated: Math.round(calibratedKg * 1000) / 1000,
      factor: Math.round(factor * 1000) / 1000,
      rawTotal: totals.total
    })
  }

  return calibrated
}

// =============================================================================
// MAIN GENERATION FUNCTION
// =============================================================================

/**
 * Generate unified demand forecasts for all strains
 *
 * @param {number} horizonDays - Days ahead to forecast (default 180)
 * @returns {Promise<Object>} { success, forecastsGenerated, error }
 */
export async function generateUnifiedForecast(horizonDays = CONFIG.HORIZON_DAYS) {
  console.log(`[demandForecastService] Starting forecast generation for ${horizonDays} days`)

  try {
    // Calculate date range
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const startDate = formatDate(today)

    const endDate = new Date(today)
    endDate.setDate(endDate.getDate() + horizonDays)
    const endDateStr = formatDate(endDate)

    // Step 1: Fetch all data sources in parallel
    const [recurringDemand, predictionDemand, strainLookup, rollingStats] = await Promise.all([
      fetchRecurringDemand(startDate, endDateStr),
      fetchPredictionDemand(startDate, endDateStr),
      fetchStrainLookup(),
      fetchRollingStatistics(CONFIG.CALIBRATION.ROLLING_WEEKS)
    ])

    console.log(`[demandForecastService] Fetched: ${recurringDemand.size} recurring, ${predictionDemand.size} prediction records`)

    // Step 2: Build daily demand map (date|strainId -> { recurring, predicted })
    const dailyDemand = new Map()
    const allDates = generateDateRange(startDate, endDateStr)
    const allStrainIds = [...strainLookup.values()]

    // Initialize with zeros for all date/strain combinations
    for (const date of allDates) {
      for (const strainId of allStrainIds) {
        const key = `${date}|${strainId}`
        dailyDemand.set(key, { recurring: 0, predicted: 0 })
      }
    }

    // Add recurring demand (convert strain name to ID)
    for (const [key, quantity] of recurringDemand) {
      const [date, strainName] = key.split('|')
      const strainId = strainLookup.get(strainName)
      if (!strainId) continue

      const dailyKey = `${date}|${strainId}`
      const existing = dailyDemand.get(dailyKey) || { recurring: 0, predicted: 0 }
      existing.recurring += quantity
      dailyDemand.set(dailyKey, existing)
    }

    // Add prediction demand (convert strain name to ID)
    for (const [key, quantity] of predictionDemand) {
      const [date, strainName] = key.split('|')
      const strainId = strainLookup.get(strainName)
      if (!strainId) continue

      const dailyKey = `${date}|${strainId}`
      const existing = dailyDemand.get(dailyKey) || { recurring: 0, predicted: 0 }
      existing.predicted += quantity
      dailyDemand.set(dailyKey, existing)
    }

    // Step 3: Aggregate to weekly totals for calibration
    const weeklyTotals = new Map()

    for (const [key, demand] of dailyDemand) {
      const [date, strainId] = key.split('|')
      const weekStart = getWeekStart(date)
      const weekKey = `${weekStart}|${strainId}`

      const existing = weeklyTotals.get(weekKey) || { recurring: 0, predicted: 0, total: 0 }
      existing.recurring += demand.recurring
      existing.predicted += demand.predicted
      existing.total += demand.recurring + demand.predicted
      weeklyTotals.set(weekKey, existing)
    }

    // Step 4: Apply calibration to weekly totals
    const calibratedWeekly = CONFIG.CALIBRATION.ENABLED
      ? calibrateWeeklyTotals(weeklyTotals, rollingStats)
      : weeklyTotals

    // Step 5: Distribute calibrated weekly back to daily
    const forecasts = []

    for (const [dailyKey, demand] of dailyDemand) {
      const [date, strainId] = dailyKey.split('|')
      const weekStart = getWeekStart(date)
      const weekKey = `${weekStart}|${strainId}`

      const weekData = calibratedWeekly.get(weekKey) || { calibrated: 0, factor: 1.0, rawTotal: 0 }
      const dailyRaw = demand.recurring + demand.predicted

      // Proportional distribution: if this day had X% of raw weekly total,
      // it gets X% of calibrated weekly total
      let calibratedKg
      if (weekData.rawTotal > 0) {
        const proportion = dailyRaw / weekData.rawTotal
        calibratedKg = weekData.calibrated * proportion
      } else {
        // No raw demand this week, use zero
        calibratedKg = 0
      }

      // Only create forecast if there's any demand
      if (dailyRaw > 0 || calibratedKg > 0) {
        forecasts.push({
          forecast_date: date,
          strain_id: strainId,
          recurring_kg: Math.round(demand.recurring * 1000) / 1000,
          predicted_kg: Math.round(demand.predicted * 1000) / 1000,
          calibrated_kg: Math.round(calibratedKg * 1000) / 1000,
          calibration_factor: weekData.factor,
          week_start: weekStart,
          is_actual: false,
          actual_kg: null
        })
      }
    }

    console.log(`[demandForecastService] Generated ${forecasts.length} forecast records`)

    // Step 6: Upsert to database in batches
    if (forecasts.length > 0) {
      const batchSize = 500
      for (let i = 0; i < forecasts.length; i += batchSize) {
        const batch = forecasts.slice(i, i + batchSize)

        const { error: upsertError } = await supabase
          .from('demand_forecasts')
          .upsert(batch, {
            onConflict: 'forecast_date,strain_id',
            ignoreDuplicates: false
          })

        if (upsertError) {
          console.error('[demandForecastService] Upsert error:', upsertError)
          throw upsertError
        }
      }
    }

    console.log(`[demandForecastService] Forecast generation complete`)

    return {
      success: true,
      forecastsGenerated: forecasts.length,
      dateRange: { start: startDate, end: endDateStr }
    }
  } catch (error) {
    console.error('[demandForecastService] Generation failed:', error)
    return {
      success: false,
      forecastsGenerated: 0,
      error: error.message
    }
  }
}

/**
 * Regenerate forecasts for a specific strain
 * Called when recurring orders change for that strain
 *
 * @param {string} strainId - Strain UUID to regenerate
 * @returns {Promise<Object>} { success, forecastsGenerated, error }
 */
export async function regenerateForecastsForStrain(strainId) {
  console.log(`[demandForecastService] Regenerating forecasts for strain ${strainId}`)

  // For now, regenerate all forecasts
  // Could be optimized to only regenerate the affected strain
  return generateUnifiedForecast()
}

/**
 * Backfill actual sales into forecast records
 * Called daily to populate actual_kg for past dates
 *
 * @returns {Promise<Object>} { success, rowsUpdated, error }
 */
export async function backfillActuals() {
  console.log('[demandForecastService] Starting actuals backfill')

  try {
    const { data, error } = await supabase.rpc('backfill_forecast_actuals')

    if (error) {
      console.error('[demandForecastService] Backfill error:', error)
      throw error
    }

    console.log(`[demandForecastService] Backfill complete: ${data} rows updated`)

    return {
      success: true,
      rowsUpdated: data
    }
  } catch (error) {
    console.error('[demandForecastService] Backfill failed:', error)
    return {
      success: false,
      rowsUpdated: 0,
      error: error.message
    }
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  generateUnifiedForecast,
  regenerateForecastsForStrain,
  backfillActuals
}
